import type { ICoachingStepRule } from "@bbj-types/organisation";
import type { ObjectId } from "@bbj-types/utils";
import { useBatchCreateActions } from "@root/api/services/action/batchCreateActions";
import { useGlobalContext } from "@root/context";
import { useI18nContext } from "@root/utils/organisation";
import { ModalAction } from "./index";
import { formatFormDataToAction } from "./logics/format";
import type { IModalActionForm, IModalActionResolver } from "./logics/types";

export interface IModalActionMultipleCandidatesProps {
  open: boolean;
  onSubmit: (coachingStepRule?: ICoachingStepRule) => void;
  onClose: () => void;
  candidates: readonly Exclude<IModalActionForm["candidate"], undefined | null>[] | ObjectId[];
}

export const ModalActionMultipleCandidates = ({
  open,
  onSubmit,
  onClose,
  candidates,
}: IModalActionMultipleCandidatesProps) => {
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const [, batchCreateActions] = useBatchCreateActions();

  const handleSubmit = async (formData: IModalActionResolver) => {
    try {
      const actionsToCreate: ReturnType<typeof formatFormDataToAction>[] = [];
      for (const candidate of candidates) {
        const formattedAction = formatFormDataToAction({ ...formData, candidate: candidate }, timeZone);
        actionsToCreate.push(formattedAction);
      }
      await batchCreateActions(actionsToCreate);
      const coachingStepRule = user.organisation.coachingStepRules.action.find(
        (rule) => rule.type === formData.type.value
      );
      window.publish("feedback", {
        severity: "success",
        message: `Les actions ont été enregistrées avec succès.${
          coachingStepRule ? " L'étape d'accompagnement des candidat a été mise à jour automatiquement." : ""
        }`,
      });
      onSubmit(coachingStepRule);
    } catch (error) {
      console.error("Erreur lors de la création des actions en masse :", error);
    }
  };

  return (
    <ModalAction
      open={open}
      title="Nouvelle action - Groupée"
      subtitle={`Vous avez sélectionné ${candidates.length} candidats afin de créer une action dans chacune de leur fiche.`}
      options={{
        hideCandidateField: true,
        hideConvocationText: true,
        disabledActionTypes: ["job-follow-up"],
        hideBeneficiaryComment: !!(
          user.organisation.beneficiaryConfiguration?.meetingActionTypes &&
          user.organisation.beneficiaryConfiguration?.meetingActionTypes.length === 0
        ),
      }}
      overrideSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};
